
import { motion } from 'framer-motion';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Brain, BookOpen, Zap, ArrowRight } from 'lucide-react';

interface RecommendationItem {
  id: string;
  title: string;
  description: string;
  type: 'meditation' | 'article' | 'exercise';
  link: string;
}

interface RecommendationsSectionProps {
  percentiles: {
    O: number;
    C: number;
    E: number;
    A: number;
    N: number;
  };
}

export default function RecommendationsSection({ percentiles }: RecommendationsSectionProps) {
  // Generate recommendations based on personality traits
  const recommendations: RecommendationItem[] = generateRecommendations(percentiles);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'meditation':
        return Brain;
      case 'article':
        return BookOpen;
      case 'exercise':
        return Zap;
      default:
        return Brain;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'meditation':
        return {
          gradient: 'from-purple-500 to-indigo-600',
          bg: 'bg-purple-50',
          border: 'border-purple-200 hover:border-purple-300',
          badge: 'bg-purple-100 text-purple-800 border-purple-200'
        };
      case 'article':
        return {
          gradient: 'from-blue-500 to-cyan-600',
          bg: 'bg-blue-50',
          border: 'border-blue-200 hover:border-blue-300',
          badge: 'bg-blue-100 text-blue-800 border-blue-200'
        };
      case 'exercise':
        return {
          gradient: 'from-emerald-500 to-teal-600',
          bg: 'bg-emerald-50',
          border: 'border-emerald-200 hover:border-emerald-300',
          badge: 'bg-emerald-100 text-emerald-800 border-emerald-200'
        };
      default:
        return {
          gradient: 'from-gray-500 to-gray-600',
          bg: 'bg-gray-50',
          border: 'border-gray-200 hover:border-gray-300',
          badge: 'bg-gray-100 text-gray-800 border-gray-200'
        };
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'meditation':
        return 'Meditação';
      case 'article':
        return 'Artigo';
      case 'exercise':
        return 'Exercício';
      default:
        return 'Conteúdo';
    }
  };

  const getButtonText = (type: string) => {
    switch (type) {
      case 'meditation':
        return 'Praticar Meditação';
      case 'article':
        return 'Ler Artigo';
      case 'exercise':
        return 'Ver Exercício';
      default:
        return 'Acessar';
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-3">
          Recomendações Personalizadas
        </h2>
        <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
          Conteúdos selecionados especialmente para seu perfil de personalidade
        </p>
      </div>
      
      <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {recommendations.map((item, index) => {
          const IconComponent = getTypeIcon(item.type);
          const colors = getTypeColor(item.type);
          
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="group h-full"
            >
              <Card className={`${colors.border} ${colors.bg} border-2 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] overflow-hidden relative h-full flex flex-col min-h-[320px]`}>
                {/* Gradient header accent */}
                <div className={`h-2 bg-gradient-to-r ${colors.gradient} flex-shrink-0`} />
                
                <CardHeader className="pb-3 relative flex-shrink-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`p-3 rounded-xl bg-gradient-to-r ${colors.gradient} text-white shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-base sm:text-lg font-bold text-gray-800 leading-tight break-words">
                          {item.title}
                        </CardTitle>
                        <Badge 
                          variant="outline" 
                          className={`text-xs font-semibold ${colors.badge} border mt-2 flex-shrink-0`}
                        >
                          {getTypeLabel(item.type)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0 flex-1 flex flex-col justify-between">
                  <div className="flex-1">
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed text-justify hyphens-auto break-words">
                      {item.description}
                    </p>
                  </div>
                </CardContent>
                
                <CardFooter className="pt-4 flex-shrink-0">
                  <Button 
                    asChild 
                    className={`w-full bg-gradient-to-r ${colors.gradient} hover:opacity-90 transition-all duration-300 text-white font-medium group-hover:shadow-lg`}
                  >
                    <Link to={item.link} className="flex items-center justify-center gap-2">
                      {getButtonText(item.type)}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </Link>
                  </Button>
                </CardFooter>
                
                {/* Subtle decorative element */}
                <div className={`absolute top-4 right-4 w-8 h-8 bg-gradient-to-r ${colors.gradient} rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300`} />
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// Generate personalized recommendations based on personality traits
function generateRecommendations(percentiles: Record<string, number>): RecommendationItem[] {
  const recommendations: RecommendationItem[] = [];
  
  // High Neuroticism
  if (percentiles.N > 70) {
    recommendations.push({
      id: 'med-calm',
      title: 'Meditação para Acalmar',
      description: 'Uma prática guiada para reduzir a ansiedade e promover o relaxamento em momentos de estresse.',
      type: 'meditation',
      link: '/meditacoes?filter=ansiedade'
    });
  }
  
  // Low Extraversion
  if (percentiles.E < 30) {
    recommendations.push({
      id: 'art-social',
      title: 'Desenvolvendo Habilidades Sociais',
      description: 'Técnicas para melhorar suas interações sociais de forma gradual e confortável.',
      type: 'article',
      link: '/artigos/habilidades-sociais'
    });
  }
  
  // High Openness
  if (percentiles.O > 70) {
    recommendations.push({
      id: 'med-creative',
      title: 'Meditação de Visualização Criativa',
      description: 'Explore seu potencial criativo através desta prática de visualização guiada.',
      type: 'meditation',
      link: '/meditacoes?filter=criatividade'
    });
  }
  
  // Low Conscientiousness
  if (percentiles.C < 30) {
    recommendations.push({
      id: 'ex-focus',
      title: 'Exercícios de Foco',
      description: 'Técnicas práticas para melhorar sua concentração e produtividade diária.',
      type: 'exercise',
      link: '/exercicios/foco'
    });
  }
  
  // General recommendation
  recommendations.push({
    id: 'art-personality',
    title: 'Compreendendo sua Personalidade',
    description: 'Um guia abrangente sobre como seus traços de personalidade influenciam seu bem-estar.',
    type: 'article',
    link: '/artigos/big-five-explicado'
  });
  
  return recommendations;
}
